import { useCallback, useEffect, useState } from 'react'

/**
 * Returns the first (innermost) ancestor with scrollable overflow-y.
 * We want innermost rather than outermost because `overflow-x: hidden` on a
 * parent causes the browser to compute `overflow-y: auto` (CSS spec), making
 * that parent appear scrollable even if it isn't the real scroll container.
 * Walking inward stops at the first genuine scroll container.
 */
function findScrollParent(node: HTMLElement | null): HTMLElement | null {
  let parent = node?.parentElement ?? null
  while (parent && parent !== document.body) {
    const { overflowY } = getComputedStyle(parent)
    if (/(auto|scroll)/.test(overflowY)) {
      return parent
    }
    parent = parent.parentElement
  }
  return null
}

function readScrollTop(scrollParent: HTMLElement | null): number {
  if (scrollParent) return scrollParent.scrollTop
  return Math.max(window.scrollY, document.documentElement.scrollTop, document.body.scrollTop)
}

/** True once the page scroll container has moved past `threshold` px. */
export function useStickyNavScrolled(threshold = 8): {
  ref: (node: HTMLElement | null) => void
  scrolled: boolean
} {
  const [node, setNode] = useState<HTMLElement | null>(null)
  const [scrolled, setScrolled] = useState(false)

  const ref = useCallback((el: HTMLElement | null) => {
    setNode(el)
  }, [])

  useEffect(() => {
    if (!node) return

    const scrollParent = findScrollParent(node)

    const onScroll = () => {
      setScrolled(readScrollTop(scrollParent) > threshold)
    }

    onScroll()
    // capture:true catches scroll on any nested scrollable element, not just window
    document.addEventListener('scroll', onScroll, { passive: true, capture: true })
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [node, threshold])

  return { ref, scrolled }
}
